import { Capacitor } from '@capacitor/core';

/**
 * Biometric login for the native shells (EN-5415).
 *
 * Web keeps the WebAuthn/passkey flow in the auth app (apps/auth passkey.js).
 * Native can't share it: the credential would land in the system browser's
 * storage, which the Capacitor WebView can't read, and Android's WebView has no
 * WebAuthn at all. So native stores the Descope refresh token in the device
 * keystore behind the OS biometric prompt instead.
 *
 * Login itself is unchanged — every method still runs in the auth app.
 * This only covers unlocking a session the player already established there.
 */

// Keychain/Keystore service key. Deliberately not derived from APP_DOMAIN: a
// build only ever talks to one environment, and a stable key means an env
// switch can't silently orphan a stored credential under a name we never read.
const CREDENTIAL_SERVER = 'hijack-poker-biometric';

// Mirrors the keystore entry so render paths can gate synchronously. The
// keystore is the source of truth — this is only a hint, and unlock() clears it
// whenever the two disagree.
const ENROLLED_EMAIL_KEY = 'hj_native_biometric_email';

export type BiometricUnlock =
  | { status: 'ok'; email: string; refreshToken: string }
  /**
   * The scan didn't complete — a no-match (AUTHENTICATION_FAILED) or any of
   * the cancel codes. One bucket on purpose: when a face fails to match, iOS's
   * own dialog offers only "Cancel", so a player who tried and failed reports
   * back with the same code as one who backed out immediately. Enrollment is
   * left INTACT (AC13) — the caller offers retry or password, never wipes.
   */
  | { status: 'not-completed' }
  /**
   * OS lockout after repeated failures (USER_LOCKOUT / USER_TEMPORARY_LOCKOUT).
   * Also intact (AC12): the lock lives on the device and clears with its
   * passcode; disabling our setting over it would punish the player twice.
   */
  | { status: 'locked-out' }
  /** Credential unreadable or gone — enrollment has been cleared. */
  | { status: 'unavailable' };

// Loaded on demand, matching services/appsflyer.ts — the plugin has no web
// implementation worth pulling into the browser bundle or Jest.
async function loadPlugin() {
  return import('@capgo/capacitor-native-biometric');
}

/**
 * True only when real biometric hardware is enrolled (AC10).
 *
 * `strongBiometryIsAvailable`, not `isAvailable`: the latter is also true on a
 * device with nothing but a PIN, which is exactly the case that produced a
 * lock-screen code prompt instead of a fingerprint during testing. A PIN is not
 * biometric authentication, so it must not make the feature appear available.
 */
export async function isBiometricAvailable(): Promise<boolean> {
  if (!Capacitor.isNativePlatform()) return false;
  try {
    const { NativeBiometric } = await loadPlugin();
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    return !!result.strongBiometryIsAvailable;
  } catch {
    return false;
  }
}

/**
 * WHY the sensor can't be used, when it can't — so the UI can say something
 * true instead of hiding (AC11/AC12, design pass 2026-08-19).
 *
 * `isBiometricAvailable()` answers yes/no, which is all the enrolment gate
 * needed, and hiding on "no" is what made this feature invisible to every
 * player who unlocks their phone with a passcode. These three cases want three
 * different messages and three different affordances:
 *
 *   not-enrolled → actionable: they can set up a face/finger and come back
 *   locked-out   → temporary: unlock the device with its passcode
 *   unsupported  → terminal: no sensor, nothing to offer
 *
 * Codes are the plugin's BiometricAuthError enum, verified against its iOS
 * convertToPluginErrorCode() switch: 1 UNAVAILABLE, 2 USER_LOCKOUT,
 * 3 NOT_ENROLLED.
 */
export type BiometricBlocked = 'not-enrolled' | 'locked-out' | 'unsupported';

/**
 * Open this app's page in iOS Settings — one back-tap from Face ID & Passcode.
 *
 * `app-settings:` is the URL form of `UIApplication.openSettingsURLString`;
 * Capacitor's WKWebView navigation delegate hands non-http(s) schemes to
 * UIApplication rather than loading them, which is exactly the public,
 * App-Review-safe route (the `App-Prefs:` schemes that jump straight to
 * Face ID setup are private API — see the unavailable card's design note).
 * iOS only by design: Android's equivalent needs a security-settings intent we
 * have no plugin for, so the Android card offers no button rather than a dead
 * one (BLOCKED_COPY resolves the CTA per sensor kind).
 */
export function openIOSSettings(): void {
  try {
    window.open('app-settings:', '_blank');
  } catch {
    /* the card's written path is the fallback */
  }
}

export async function getBiometricBlockedReason(): Promise<BiometricBlocked | null> {
  if (!Capacitor.isNativePlatform()) return 'unsupported';
  try {
    const { NativeBiometric } = await loadPlugin();
    const result = await NativeBiometric.isAvailable({ useFallback: false });
    if (result.strongBiometryIsAvailable) return null;
    switch (Number((result as { errorCode?: unknown }).errorCode)) {
      case 2:
        return 'locked-out';
      case 3:
        return 'not-enrolled';
      default:
        return 'unsupported';
    }
  } catch {
    return 'unsupported';
  }
}

/** Which sensor the device has, for prompt copy ("Face ID" vs "fingerprint"). */
export async function getBiometryLabel(): Promise<string> {
  if (!Capacitor.isNativePlatform()) return 'biometrics';
  try {
    const { NativeBiometric, BiometryType } = await loadPlugin();
    const { biometryType } = await NativeBiometric.isAvailable({ useFallback: false });
    switch (biometryType) {
      case BiometryType.FACE_ID:
      case BiometryType.FACE_AUTHENTICATION:
        return 'Face ID';
      case BiometryType.TOUCH_ID:
        return 'Touch ID';
      case BiometryType.FINGERPRINT:
        return 'fingerprint';
      case BiometryType.IRIS_AUTHENTICATION:
        return 'iris';
      default:
        return 'biometrics';
    }
  } catch {
    return 'biometrics';
  }
}

/** Synchronous hint for render gating. Cheap, and never trusted for auth. */
export function isBiometricEnrolled(): boolean {
  try {
    return !!localStorage.getItem(ENROLLED_EMAIL_KEY);
  } catch {
    return false;
  }
}

export function getEnrolledEmail(): string | null {
  try {
    return localStorage.getItem(ENROLLED_EMAIL_KEY);
  } catch {
    return null;
  }
}

/**
 * Store the refresh token behind a biometric-bound Keystore key.
 *
 * BIOMETRY_CURRENT_SET is AC9 expressed in hardware: the OS invalidates the
 * credential the moment the enrolled biometric set changes, so a fingerprint
 * or face added after enrollment can't unlock someone else's stored token.
 *
 * Cost of that guarantee: both the read (unlockBiometric's
 * getSecureCredentials) and this write are separate authenticated key
 * operations, so a biometric login prompts TWICE — once to decrypt the
 * token, once more moments later to re-encrypt the rotated token via
 * refreshStoredToken(). There was a prior attempt to collapse this to one
 * prompt with `authValidityDuration` (a short window after which a key
 * operation can skip re-prompting) — removed deliberately, not reinstated:
 * the plugin's Android implementation resolves that window with
 * tryWithoutPrompt(), which is satisfied by ANY qualifying device
 * authentication during the window, including just unlocking the phone, not
 * necessarily the enrolled biometric. That silently defeats AC9 again from
 * the other direction, so the two-prompt cost stands.
 *
 * The plugin is PINNED to exactly 8.5.0. The 8.6.x line regressed the
 * accessControl path: 8.6.0 replaced KeyProperties.AUTH_BIOMETRIC_STRONG with
 * hand-rolled auth-type constants whose values don't match Android's (and
 * include a "weak" type Keystore doesn't have), so the key is created with an
 * illegal DEVICE_CREDENTIAL|BIOMETRIC_STRONG + per-operation-timeout
 * combination. Samsung's Keymaster then fails cipher.doFinal() with a null
 * message — for BOTH access-control modes (reproduced on an S24 / Android
 * 16). Do not bump past 8.5.0 until upstream fixes AuthActivity's KEY_AUTH_*
 * values (compare them against NativeBiometric.java:856, which uses the real
 * constant).
 */
export type EnrollResult = { ok: boolean; error?: string; code?: number };

/**
 * Write the credential. Shared by enrollment and the post-login re-arm
 * (refreshStoredToken) — both are authenticated key operations because of
 * accessControl, so both prompt (see the doc comment above).
 *
 * That prompt IS the enrollment confirmation now — see enrollBiometric's own
 * doc comment for why enrollment no longer calls verifyIdentity() first.
 * Cancellation is detected here (not left to the generic failure() catch) so
 * a player backing out mid-enrollment, or a rotated-token re-arm interrupted
 * mid-login, both report "cancelled" distinctly from a real keystore
 * failure — the same distinction a separate verifyIdentity() step used to
 * make on its own.
 */
async function storeCredential(email: string, refreshToken: string): Promise<EnrollResult> {
  if (!Capacitor.isNativePlatform() || !email || !refreshToken) {
    return { ok: false, error: 'missing-session' };
  }
  try {
    const { NativeBiometric, AccessControl } = await loadPlugin();
    await NativeBiometric.setCredentials({
      username: email,
      password: refreshToken,
      server: CREDENTIAL_SERVER,
      accessControl: AccessControl.BIOMETRY_CURRENT_SET,
      title: 'Enable biometric login',
      negativeButtonText: 'Cancel'
    });
    try {
      localStorage.setItem(ENROLLED_EMAIL_KEY, email);
    } catch {
      /* hint only \u2014 the keystore entry is what matters */
    }
    return { ok: true };
  } catch (err) {
    if (await isCancellation(err)) return { ok: false, error: 'cancelled' };
    return failure(err);
  }
}

/**
 * Turn biometric login on.
 *
 * ONE prompt, not two (was two — see the 2026-08-19 history below; changed
 * since storeCredential's write stopped being a silent operation).
 *
 * This used to call verifyIdentity() first as an explicit "confirm it's you"
 * gate before storeCredential's write, back when that write had no
 * accessControl of its own: enrolling would otherwise complete with zero
 * scan, zero confirmation, and iOS's one-time Face ID permission alert
 * arriving disconnected from the decision at some later login instead.
 *
 * storeCredential's write is now itself an accessControl-gated, authenticated
 * operation — it prompts with its own clear "Enable biometric login" title on
 * every call, enrollment included. The separate verifyIdentity() step became
 * a second, redundant scan of the same fingerprint immediately before the one
 * that actually matters, with no remaining case it alone covers — removed.
 */
export async function enrollBiometric(email: string, refreshToken: string): Promise<EnrollResult> {
  if (!Capacitor.isNativePlatform() || !email || !refreshToken) {
    return { ok: false, error: 'missing-session' };
  }
  return storeCredential(email, refreshToken);
}

// Surfaced to the caller rather than swallowed: "cancelled or unavailable" covers
// a user cancel, a keystore that rejected the key, and a prompt the system
// dismissed, which need completely different fixes.
function failure(err: unknown): EnrollResult {
  const error = (err as Error)?.message || String(err);
  const code = Number((err as { code?: unknown })?.code);
  console.warn('[Biometrics] enroll failed:', { code, error, err });
  return { ok: false, error, code: Number.isNaN(code) ? undefined : code };
}

/**
 * Prompt for the biometric and return the stored token.
 *
 * getSecureCredentials() is the read half of the accessControl-protected key
 * (see storeCredential's doc comment): decrypting it is itself an
 * authenticated key operation, so this single call both prompts the sensor
 * and returns the token, with no separate verifyIdentity() step here.
 *
 * 'cancelled' is distinguished from 'unavailable' because they mean different
 * things to the caller: a cancel should leave the enrollment intact and fall
 * back to password (AC6), while anything else means the credential can no
 * longer be used — biometrics changed, credential invalidated, lockout — and
 * the enrollment is cleared so the player is put back on password (AC9).
 */
export async function unlockBiometric(): Promise<BiometricUnlock> {
  if (!Capacitor.isNativePlatform() || !isBiometricEnrolled()) {
    return { status: 'unavailable' };
  }
  try {
    const { NativeBiometric } = await loadPlugin();
    const credentials = await NativeBiometric.getSecureCredentials({
      server: CREDENTIAL_SERVER,
      reason: 'Sign in to Hijack Poker',
      title: 'Sign in to Hijack Poker',
      subtitle: 'Confirm it\u2019s you to continue'
    });
    if (!credentials?.password) {
      await disableBiometric();
      return { status: 'unavailable' };
    }
    return {
      status: 'ok',
      email: credentials.username,
      refreshToken: credentials.password
    };
  } catch (err) {
    // AC13 (Noe, QA 2026-08-17, re-tested 8/18): a failed or cancelled scan
    // used to fall through to the hard-error branch below, which DISABLED the
    // enrollment and shipped the player to the login page — the exact
    // "auto-disable + redirect" pair QA filed. A no-match is a normal event,
    // not a broken credential: the enrollment stays, the caller offers the
    // two honest paths.
    if (await isCancellation(err)) return { status: 'not-completed' };
    if (await isLockout(err)) return { status: 'locked-out' };
    console.warn('[Biometrics] unlock failed:', (err as Error)?.message || err);
    // Fail closed: an unreadable credential is treated as gone rather than
    // retried, so the player lands on the password form instead of a loop.
    await disableBiometric();
    return { status: 'unavailable' };
  }
}

async function isCancellation(err: unknown): Promise<boolean> {
  try {
    const { BiometricAuthError } = await loadPlugin();
    const code = Number((err as { code?: unknown })?.code);
    return (
      code === BiometricAuthError.USER_CANCEL ||
      code === BiometricAuthError.SYSTEM_CANCEL ||
      code === BiometricAuthError.APP_CANCEL ||
      code === BiometricAuthError.USER_FALLBACK ||
      // A genuine no-match. Grouped with the cancels because iOS reports a
      // plain cancel for "failed twice, pressed the only button offered" —
      // the two are indistinguishable in practice, so they must lead to the
      // same place (see the enrollment soft landing for the same reasoning).
      code === BiometricAuthError.AUTHENTICATION_FAILED
    );
  } catch {
    return false;
  }
}

async function isLockout(err: unknown): Promise<boolean> {
  try {
    const { BiometricAuthError } = await loadPlugin();
    const code = Number((err as { code?: unknown })?.code);
    return (
      code === BiometricAuthError.USER_LOCKOUT || code === BiometricAuthError.USER_TEMPORARY_LOCKOUT
    );
  } catch {
    return false;
  }
}

/**
 * Descope rotates the refresh token on every exchange, so the copy in the
 * keystore goes stale the moment the app refreshes. Re-arming after each
 * successful login is what keeps biometric sign-in working past the first use.
 */
export async function refreshStoredToken(email: string, refreshToken: string): Promise<EnrollResult> {
  if (!isBiometricEnrolled() || !refreshToken) return { ok: true };
  const enrolled = getEnrolledEmail();
  if (enrolled && email && enrolled !== email) return { ok: true };
  // storeCredential, NOT enrollBiometric: this runs after every login and must
  // never prompt (that pairing is what made enrolment silent — see above).
  return storeCredential(enrolled || email, refreshToken);
}

/**
 * Clears the local "enrolled" hint unconditionally, even when the underlying
 * keystore delete fails below — callers that already know the stored
 * credential is dead (a Descope rejection, a failed re-arm) rely on that to
 * stop retrying a doomed unlock. The returned `ok` tells an explicit
 * disable-biometrics caller (Settings) whether the keystore entry actually
 * got removed, so it can be honest about a leftover credential instead of
 * reporting an unconditional success.
 */
export async function disableBiometric(): Promise<{ ok: boolean }> {
  try {
    localStorage.removeItem(ENROLLED_EMAIL_KEY);
  } catch {
    /* noop */
  }
  if (!Capacitor.isNativePlatform()) return { ok: true };
  try {
    const { NativeBiometric } = await loadPlugin();
    await NativeBiometric.deleteCredentials({ server: CREDENTIAL_SERVER });
    return { ok: true };
  } catch (err) {
    console.warn('[Biometrics] delete failed:', (err as Error)?.message || err);
    return { ok: false };
  }
}
